import isEmpty from 'lodash/isEmpty';
import upperCase from 'lodash/upperCase';
import React from 'react';
import { Button, ButtonGroup } from 'reactstrap';

import ListAgreements from '../../agreements/ListAgreements';
import ActiveView from '../../associations/Association/ActiveView';
import { publicStatuses as campaignStatuses } from '../../campaigns/constants';
import ListMyCampaigns from '../../campaigns/ListMyCampaigns';
import SwimlaneCampaigns from '../../campaigns/SwimlaneCampaigns';
import Tabs from '../../generic/Tabs';
import ListCategorizedInvoices from '../../invoices/ListCategorizedInvoices';
import { stages as opportunityStages } from '../../opportunities/constants';
import ListCategorizedOpportunities from '../../opportunities/ListCategorizedOpportunities';
import SwimlaneOpportunities from '../../opportunities/SwimlaneOpportunities';
import { sessionShape } from '../../prop-types';
import withSessionInfo from '../../signin/with-session-info';
import { getPreference, setPreference } from '../../utils/preferences';
import {
  AGREEMENTS,
  CAMPAIGNS,
  DEFAULT_VIEW_MODE,
  INVOICES,
  OPPORTUNITIES,
  VIEW_MODES,
} from '../constants';

function getViewMode(key) {
  return getPreference(`DASHBOARD_VIEW_MODE_${upperCase(key)}`);
}

function setViewMode(key, value) {
  return setPreference(`DASHBOARD_VIEW_MODE_${upperCase(key)}`, value);
}

function tabs(currentViewMode, salesTeam, teamMemberId) {
  const opportunitiesView =
    currentViewMode === 'swimlane' ? (
      <SwimlaneOpportunities
        categories={opportunityStages}
        groupBy="stage"
        relevantFor={teamMemberId}
        relevantForSalesTeam={salesTeam}
      />
    ) : (
      <ListCategorizedOpportunities
        hiddenColumns={['Owner']}
        relevantFor={teamMemberId}
        relevantForSalesTeam={salesTeam}
        showBudgets
      />
    );

  const campaignsView =
    currentViewMode === 'swimlane' ? (
      <SwimlaneCampaigns
        categories={campaignStatuses}
        groupBy="status"
        relevantFor={teamMemberId}
        relevantForSalesTeam={salesTeam}
      />
    ) : (
      <ListMyCampaigns
        relevantFor={teamMemberId}
        relevantForSalesTeam={salesTeam}
      />
    );

  const invoicesView = (
    <ListCategorizedInvoices
      title=""
      ownerId={teamMemberId}
      salesTeam={salesTeam}
      hiddenColumns={['Owner']}
    />
  );

  const agreementsList = (
    <ListAgreements
      title=""
      relevantFor={teamMemberId}
      relevantForSalesTeam={salesTeam}
    />
  );

  return [
    {
      label: "My team's Agreements",
      value: AGREEMENTS,
      hasSwimlaneViewMode: false,
      content: agreementsList,
    },
    {
      label: "My team's Opportunities",
      value: OPPORTUNITIES,
      hasSwimlaneViewMode: true,
      content: opportunitiesView,
    },
    {
      label: "My team's Campaigns",
      value: CAMPAIGNS,
      hasSwimlaneViewMode: true,
      content: campaignsView,
    },
    {
      label: "My team's Invoices",
      value: INVOICES,
      hasSwimlaneViewMode: false,
      content: invoicesView,
    },
  ];
}

class LeadDashboard extends React.PureComponent {
  constructor(props) {
    super(props);

    if (isEmpty(getViewMode(OPPORTUNITIES))) {
      setViewMode(OPPORTUNITIES, DEFAULT_VIEW_MODE.name);
    }
    if (isEmpty(getViewMode(CAMPAIGNS))) {
      setViewMode(CAMPAIGNS, DEFAULT_VIEW_MODE.name);
    }

    this.state = {
      currentViewMode: getViewMode(OPPORTUNITIES),
      tabWithSwimlaneViewModeSelected: true,
      activeTabId: OPPORTUNITIES,
      relevantForTeamMember: undefined,
    };

    this.handleViewModeSelect = this.handleViewModeSelect.bind(this);
    this.activeTab = this.activeTab.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.handleRelevantUserSelect = this.handleRelevantUserSelect.bind(this);
  }

  handleViewModeSelect(event) {
    const { value: currentViewMode } = event.currentTarget;
    this.setViewMode(currentViewMode);
  }

  handleTabSelect(activeTabId) {
    const activeTab = this.activeTab(activeTabId);
    this.setState({
      tabWithSwimlaneViewModeSelected: activeTab.hasSwimlaneViewMode,
      activeTabId: activeTab.value,
      currentViewMode: getViewMode(activeTabId),
    });
  }

  handleRelevantUserSelect(userId) {
    this.setState({ relevantForTeamMember: userId });
  }

  setViewMode(currentViewMode) {
    this.setState({ currentViewMode });
    setViewMode(this.state.activeTabId, currentViewMode);
  }

  activeTab(activeTabId) {
    const { session } = this.props;
    const { currentViewMode } = this.state;
    const allTabs = tabs(currentViewMode, session.user.id);

    return allTabs.find(tab => tab.value === activeTabId);
  }

  isViewModeButtonActive(buttonViewMode) {
    const { currentViewMode, tabWithSwimlaneViewModeSelected } = this.state;

    if (buttonViewMode === 'swimlane') {
      return (
        buttonViewMode === currentViewMode && tabWithSwimlaneViewModeSelected
      );
    }
    return (
      buttonViewMode === currentViewMode || !tabWithSwimlaneViewModeSelected
    );
  }

  render() {
    const { session } = this.props;

    if (!(session && session.user)) return null; // user info not yet loaded

    const { currentViewMode, tabWithSwimlaneViewModeSelected } = this.state;
    return (
      <Tabs
        tabs={tabs(
          currentViewMode,
          session.user.salesTeam,
          this.state.relevantForTeamMember,
        )}
        onTabChange={this.handleTabSelect}
        defaultActive={OPPORTUNITIES}
        id="leadDashboard"
      >
        <div className="d-inline-flex">
          <ActiveView
            isMulti={false}
            id="relevantForTeamMember"
            name="relevantForTeamMember"
            value={this.state.relevantForTeamMember}
            type="user"
            placeholder="Display data for one team member only"
            searchParams={{ salesTeam: session.user.salesTeam }}
            onChange={event => {
              this.handleRelevantUserSelect(event.target.value || undefined);
            }}
            onSelectBlur={() => {}}
            style={{ width: '360px' }}
          />
          {tabWithSwimlaneViewModeSelected && (
            <ButtonGroup className="ml-2">
              {VIEW_MODES.map(({ name: buttonViewMode, icon: Icon }) => (
                <Button
                  key={buttonViewMode}
                  title={`Switch to ${buttonViewMode} view`}
                  value={buttonViewMode}
                  size="sm"
                  outline
                  onClick={this.handleViewModeSelect}
                  active={this.isViewModeButtonActive(buttonViewMode)}
                >
                  <Icon />
                </Button>
              ))}
            </ButtonGroup>
          )}
        </div>
      </Tabs>
    );
  }
}

LeadDashboard.propTypes = {
  session: sessionShape,
};

LeadDashboard.defaultProps = {
  session: null,
};

export default withSessionInfo(LeadDashboard);
